import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router';
import axios from '|/helpers/axios';
import { getCookie, setCookie } from '|/helpers/cookies';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import { shouldDisplayOnPage } from './utils';

type OutageBannerConfig = {
  isEnabled?: boolean;
  outageHeader?: string;
  outageDescription?: string;
  outageCookieName?: string;
  excludedPages?: string[];
  includedPages?: string[];
};

type HeaderApiResponse = {
  appSettings?: {
    OutageBanner?: OutageBannerConfig;
  };
};

let cachedConfig: OutageBannerConfig | null | undefined = undefined;
let configPromise: Promise<OutageBannerConfig | null> | null = null;

export const resetCache = () => {
  cachedConfig = undefined;
  configPromise = null;
};

const fetchOutageBannerConfig = (): Promise<OutageBannerConfig | null> => {
  if (cachedConfig !== undefined) {
    return Promise.resolve(cachedConfig);
  }

  if (!configPromise) {
    configPromise = axios
      .get<HeaderApiResponse>(`${process.env.HEADER_API_URL}/v2/header?keys=appsettings`)
      .then(r => {
        cachedConfig = r.data.appSettings?.OutageBanner || null;
        return cachedConfig;
      })
      .catch(error => {
        console.error('Failed to fetch outage banner config:', error);
        configPromise = null;
        return null;
      });
  }
  return configPromise;
};

const useOutageBannerConfig = () => {
  const { pathname } = useLocation();
  const { trackEvent } = useAnalyticsContext();
  const [bannerConfig, setBannerConfig] = useState<OutageBannerConfig | null>(
    cachedConfig !== undefined ? cachedConfig : null
  );
  const [isLoading, setIsLoading] = useState(cachedConfig === undefined);
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    fetchOutageBannerConfig().then(config => {
      setBannerConfig(config);
      setIsLoading(false);
    });
  }, []);

  const header = bannerConfig?.outageHeader || 'SORRY, WE ARE EXPERIENCING AN OUTAGE';
  const description = bannerConfig?.outageDescription || 'Our team is working hard to fix this';
  const dismissCookie = bannerConfig?.outageCookieName || 'carvana-outage-cookie-banner';

  const dismissCookieValue = getCookie(dismissCookie);

  useEffect(() => {
    if (!dismissCookieValue) {
      setCookie(dismissCookie, 'true');
    }
  }, [dismissCookie, dismissCookieValue]);

  const shouldShowBanner = () => {
    if (!bannerConfig?.isEnabled) return false;

    // Banner renders once at Root, so it must self-restrict to the routes that
    // actually mount a purchase/checkout header — otherwise it leaks onto pages
    // (e.g. /account, the catch-all redirect) that never showed it pre-Root.
    const isHeaderRoute =
      pathname === '/' ||
      pathname.startsWith('/dashboard') ||
      pathname.startsWith('/next-step') ||
      pathname.startsWith('/checkout') ||
      (pathname.startsWith('/steps/') && !pathname.startsWith('/steps/order-placed'));

    if (!isHeaderRoute) return false;

    const includedPages = bannerConfig.includedPages || [];
    const excludedPages = bannerConfig.excludedPages || [];

    return shouldDisplayOnPage(includedPages, excludedPages, pathname);
  };

  const showBanner = shouldShowBanner();

  useEffect(() => {
    if (showBanner && !isLoading && dismissCookieValue !== 'false') {
      trackEvent('header - banner - viewed banner', {
        bannerType: 'outage-banner',
        header,
        description,
        context: 'purchase-flow',
        currentPath: pathname
      });
    }
  }, [showBanner, isLoading, dismissCookieValue, header, description, trackEvent, pathname]);

  const handleDismiss = useCallback(() => {
    trackEvent('header - banner - dismissed banner', {
      bannerType: 'outage-banner',
      header,
      description,
      context: 'purchase-flow',
      currentPath: pathname
    });

    setCookie(dismissCookie, 'false');
    setIsHidden(true);
  }, [trackEvent, header, description, pathname, dismissCookie]);

  const bannerDismissed = dismissCookieValue === 'false';

  const finalIsVisible = !isLoading && showBanner && !bannerDismissed && !isHidden;

  return {
    isVisible: finalIsVisible,
    header,
    description,
    onDismiss: handleDismiss
  };
};

export default useOutageBannerConfig;
